import {
  ActionIcon,
  Alert,
  Button,
  ButtonProps,
  Center,
  CheckboxGroup,
  Drawer,
  Group,
  Image,
  Input,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
  useMantineTheme,
} from "@mantine/core";
import {
  useDebouncedValue,
  useDisclosure,
  useElementSize,
  useMediaQuery,
} from "@mantine/hooks";
import { HeadersArgs, LoaderFunction, MetaFunction } from "@remix-run/node";
import {
  Link,
  useLoaderData,
  useLocation,
  useNavigation,
} from "@remix-run/react";
import {
  IconInfoCircle,
  IconMicroscope,
  IconPlus,
  IconSearch,
  IconTrash,
  IconVaccine,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";

import { BookHealthCareHero } from "~/components/book-healthcare-hero";
import { LabCard } from "~/components/lab-card";
import {
  COLLECTION_PRICE,
  DEFAULT_OG_IMAGE_EN,
  DEFAULT_OG_IMAGE_FR,
  PAGE_TITLE,
} from "~/constants";
import { useTranslation } from "~/hooks/use-typed-translation";
import i18n from "~/localization/i18n";
import i18nextServer from "~/localization/i18n.server";
import { getLabServices } from "~/services/get-lab-services";
import utilityClasses from "~/styles/utilities.module.scss";
import { ComboboxItemPrice, OkDataError } from "~/types";
import { Lab } from "~/types/lab";
import { resolveCurrencyContext } from "~/utils/currency.server";
import { formatCurrency } from "~/utils/format-currency";
import { getLabsSubTotal } from "~/utils/get-labs-subtotal";
import { getCentralizedMeta } from "~/utils/meta-helper";
import { normalizeString } from "~/utils/normalize-string";
import { shouldNameWrap } from "~/utils/should-name-wrap";

import classes from "./lab.module.scss";

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  if (!data) return [];
  
  return getCentralizedMeta({
    title: (data as any).title,
    description: (data as any).description,
    image: (data as any).locale === "fr" ? DEFAULT_OG_IMAGE_FR : DEFAULT_OG_IMAGE_EN,
    url: (data as any).origin + location.pathname,
    locale: (data as any).locale,
    keywords: "Ivory Coast, Côte d'Ivoire, Lab, Test, Appointment, Medical analysis",
    alternateLanguages: {
      en: `${(data as any).origin}/en/lab`,
      fr: `${(data as any).origin}/fr/lab`,
    }
  });
};

export const headers = ({ loaderHeaders }: HeadersArgs) => {
  return loaderHeaders;
};

// All units are in pixels
const microscopeIconSize = 45;
const cardInlinePadding = 16 * 2;
const trashIconSize = 28;
const innerGap = 12;
const totalUnavailableSpace =
  microscopeIconSize + cardInlinePadding + trashIconSize + innerGap;

const ITEMS_PER_PAGE = 20;
const ADD_MORE = 40;

export default function Route() {
  const { t, i18n } = useTranslation();
  const navigation = useNavigation();
  const { state } = useLocation();
  const { breakpoints } = useMantineTheme();

  const loaderData = useLoaderData() as any;
  const allLabs = loaderData?.data;
  const currency = loaderData?.currency;
  const collectionPrice = loaderData?.collectionPrice;

  const isSmall = useMediaQuery(`(min-width: ${breakpoints.sm})`);
  const isLarge1 = useMediaQuery(`(min-width: ${breakpoints.lg1})`);
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedLabs, setSelectedLabs] = useState<string[]>(
    state?.selectedLabs ?? []
  );
  const { ref: rightSectionRef, width: rightSectionWidth } = useElementSize();
  const drawerWidth = isSmall ? 400 : 500;
  const [filteredLabs, setFilteredLabs] = useState<Lab[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search.toLocaleLowerCase(), 400);

  // Pagination state
  const [displayedCount, setDisplayedCount] = useState(ITEMS_PER_PAGE); // Initial number of labs to show

  const subTotal = useMemo(() => {
    return getLabsSubTotal(selectedLabs, allLabs ?? []);
  }, [selectedLabs, allLabs]);

  const requiredServices = useMemo(() => {
    if (!selectedLabs.length || !allLabs?.length) return [];

    return selectedLabs
      .map(value => allLabs.find((item: Lab) => item.value === value))
      .filter((lab: Lab | undefined) => lab && lab.collectionMethod === "sang")
      .map((lab: Lab | undefined) => lab!.label);
  }, [selectedLabs, allLabs]);

  // Memoized labs to display (with pagination logic)
  const labsToDisplay = useMemo(() => {
    if (!filteredLabs?.length) return [];

    // If searching, show all filtered results (no pagination)
    if (debouncedSearch) {
      return filteredLabs;
    }

    // If not searching, apply pagination
    return filteredLabs.slice(0, displayedCount);
  }, [filteredLabs, displayedCount, debouncedSearch]);

  // Check if there are more labs to show
  const hasMoreLabs = useMemo(() => {
    if (!filteredLabs?.length || debouncedSearch) return false;
    return displayedCount < filteredLabs.length;
  }, [filteredLabs, displayedCount, debouncedSearch]);

  const handleSeeMore = () => {
    setDisplayedCount(prev => prev + ADD_MORE);
  };

  useEffect(() => {
    if (selectedLabs.length) {
      // Do nothing for now
    } else {
      close();
    }
  }, [selectedLabs, close]);

  useEffect(() => {
    if (debouncedSearch && allLabs?.length) {
      const filtered = allLabs.filter((item: Lab) =>
        normalizeString(item.label).includes(normalizeString(debouncedSearch))
      );
      setFilteredLabs(filtered);
      // Reset pagination when searching
      setDisplayedCount(ITEMS_PER_PAGE);
    } else {
      setFilteredLabs(allLabs);
      // Reset pagination when clearing search
      setDisplayedCount(ITEMS_PER_PAGE);
    }
  }, [debouncedSearch, allLabs]);

  return (
    <div className={classes.section}>
      <div className={utilityClasses["container"]}>
        <BookHealthCareHero heading={t("lab-page-heading")} headingOrder={1} />

        <div className={classes["flex-content"]}>
          <Stack gap={"1rem"} className={classes["left-section"]}>
            <TextInput
              type="search"
              placeholder={t("search-for-lab-test")}
              leftSection={<IconSearch size={18} />}
              rightSection={
                search !== "" ? (
                  <Input.ClearButton onClick={() => setSearch("")} />
                ) : null
              }
              value={search}
              onChange={val => setSearch(val.currentTarget.value as string)}
              styles={{
                input: {
                  paddingBlock: "1.5em",
                },
              }}
            />
            <CheckboxGroup value={selectedLabs} onChange={setSelectedLabs}>
              <Group gap={isSmall ? "1.5rem" : "1rem"} align="start" w="100%">
                {labsToDisplay?.length ? (
                  labsToDisplay.map((lab, idx) => (
                    <LabCard
                      key={`${lab.value}-${idx}`} // Better key for potential duplicates
                      name={lab.label}
                      value={lab.value}
                      price={lab.price ?? 0}
                      isChecked={selectedLabs.includes(lab.value)}
                      search={search}
                    />
                  ))
                ) : (
                  <Center w="100%" mt="3rem">
                    <Text c="dimmed" fz={14}>
                      {t("no-match-lab-test")}
                    </Text>
                  </Center>
                )}
              </Group>

              {/* See More Button */}
              {hasMoreLabs && (
                <Center w="100%" mt="3rem">
                  <Button variant="outline" onClick={handleSeeMore}>
                    {t("view-more")}
                    {/* ({(filteredLabs?.length ?? 0) - displayedCount}) */}
                  </Button>
                </Center>
              )}
            </CheckboxGroup>
          </Stack>

          <Paper
            ref={rightSectionRef}
            withBorder
            className={classes["right-section"]}
            p="1rem"
            mih={250}>
            <Title order={2} fw={600} fz={18}>
              {t("selected-labs")} ({selectedLabs.length})
            </Title>

            {selectedLabs.length > 0 ? (
              <Stack gap="1.5rem" mt="1rem">
                <Stack gap="0.5rem">
                  {selectedLabs.map((value, idx) => {
                    const foundLab = allLabs?.find(
                      (item: Lab) => item.value === value
                    );

                    const availableSpace =
                      rightSectionWidth - totalUnavailableSpace;
                    const shouldWrap = shouldNameWrap(
                      foundLab?.label as string,
                      availableSpace,
                      20
                    );
                    return (
                      <Group
                        style={{
                          borderBottom: "1px solid var(--mantine-color-gray-2)",
                        }}
                        py="1rem"
                        justify="space-between"
                        key={idx}
                        gap={0}
                        pos={"relative"}>
                        <Group gap="0.75rem">
                          <div className={classes["icon-wrapper"]}>
                            <IconMicroscope
                              size={23}
                              stroke={1.5}
                              color="white"
                            />
                          </div>
                          <Stack
                            gap={3}
                            className={`${classes["text-wrapper"]} ${
                              shouldWrap ? classes.wrap : ""
                            }`}>
                            <Text
                              fz={15}
                              fw={500}
                              className={`${classes.name} ${
                                shouldWrap ? classes.wrap : ""
                              }`}>
                              {foundLab?.label}
                            </Text>
                            <Text fz={14} c="dimmed">
                              {formatCurrency(
                                foundLab?.price ?? 0,
                                i18n.language,
                                currency
                              )}
                            </Text>
                          </Stack>
                        </Group>
                        <ActionIcon
                          pos={"absolute"}
                          aria-label={t("remove-selected-lab")}
                          title={t("remove-selected-lab")}
                          right={0}
                          variant="subtle"
                          color="red"
                          radius={"xl"}
                          onClick={() =>
                            setSelectedLabs(
                              selectedLabs.filter(
                                (labValue: string) => labValue !== value
                              )
                            )
                          }>
                          <IconTrash size={18} stroke={1.5} />
                        </ActionIcon>
                      </Group>
                    );
                  })}
                </Stack>

                {requiredServices.length ? (
                  <Stack mt={"1rem"}>
                    <Title order={3} fw={600} fz={16}>
                      {t("required-services")}
                    </Title>

                    <Alert
                      variant="light"
                      color={"blue"}
                      radius={"sm"}
                      maw={rightSectionWidth}
                      icon={<IconInfoCircle />}>
                      <Text fz={14} c={"blue"}>
                        {t("required-services-note")}
                      </Text>
                    </Alert>

                    <Group
                      pb={"1rem"}
                      justify="space-between"
                      pos={"relative"}
                      style={{
                        borderBottom: "1px solid var(--mantine-color-gray-2)",
                      }}>
                      <Group gap="0.75rem">
                        <div className={classes["icon-wrapper"]}>
                          <IconVaccine size={23} stroke={1.5} color="white" />
                        </div>
                        <Stack gap={3} className={`${classes["text-wrapper"]}`}>
                          <Text fz={15} fw={500} className={`${classes.name}`}>
                            {t("blood-collection")}
                          </Text>
                          <Text fz={14} c="dimmed">
                            {formatCurrency(
                              collectionPrice,
                              i18n.language,
                              currency
                            )}
                          </Text>
                        </Stack>
                      </Group>
                    </Group>

                    <Text fz={14} c="dimmed">
                      <Text inherit fw={600} span>
                        {t("required-for")}:
                      </Text>{" "}
                      {requiredServices.join(", ")}
                    </Text>
                  </Stack>
                ) : null}

                <Group justify="space-between" mt={"1rem"}>
                  <Text fw={500} fz={15}>
                    {t("subtotal")}:
                  </Text>
                  <Text fw={500} fz={15}>
                    {formatCurrency(
                      subTotal + (requiredServices.length ? collectionPrice : 0),
                      i18n.language,
                      currency
                    )}
                  </Text>
                </Group>
                <ProceedToBookButton
                  labs={allLabs ?? []}
                  selectedLabs={selectedLabs}
                  loading={navigation.state === "loading"}
                />
              </Stack>
            ) : (
              <Center mt="5rem">
                <Text fz={14} c="dimmed">
                  {t("no-lab-tests-selected")}
                </Text>
              </Center>
            )}
          </Paper>

          <Drawer
            display={{ base: "block", lg1: "none" }}
            size={drawerWidth}
            position={isSmall ? "right" : "bottom"}
            radius={isSmall ? "15" : "15 15 0 0"}
            offset={isSmall ? 8 : 0}
            opened={isLarge1 ? false : opened}
            onClose={close}
            title={
              <Text span fz={16} fw={500}>
                {t("filters")}
              </Text>
            }
            withCloseButton>
            <Stack gap="1.5rem">
              <Stack gap={"0.5rem"}>
                {selectedLabs.map((value, idx) => {
                  const foundLab = allLabs?.find((item: Lab) => item.value === value);
                  const availableSpace = drawerWidth - totalUnavailableSpace;
                  const shouldWrap = shouldNameWrap(
                    foundLab?.label as string,
                    availableSpace,
                    25
                  );
                  return (
                    <Group
                      py={"1rem"}
                      style={{
                        borderBottom: "1px solid var(--mantine-color-gray-2)",
                      }}
                      justify="space-between"
                      key={idx}
                      gap={0}
                      pos={"relative"}>
                      <Group gap="0.75rem">
                        <div className={classes["icon-wrapper"]}>
                          <IconMicroscope
                            size={23}
                            stroke={1.5}
                            color="white"
                          />
                        </div>
                        <Stack
                          gap={3}
                          className={`${classes["text-wrapper"]} ${
                            shouldWrap ? classes.wrap : ""
                          }`}>
                          <Text
                            fz={15}
                            fw={500}
                            className={`${classes.name} ${
                              shouldWrap ? classes.wrap : ""
                            }`}>
                            {foundLab?.label}
                          </Text>
                          <Text fz={14} c="dimmed">
                            {formatCurrency(
                              foundLab?.price ?? 0,
                              i18n.language,
                              currency
                            )}
                          </Text>
                        </Stack>
                      </Group>
                      <ActionIcon
                        pos={"absolute"}
                        aria-label="Remove selected lab"
                        title="Remove selected lab"
                        right={0}
                        variant="subtle"
                        color="red"
                        radius={"xl"}
                        onClick={() =>
                          setSelectedLabs(
                            selectedLabs.filter((labValue: string) => labValue !== value)
                          )
                        }>
                        <IconTrash size={18} stroke={1.5} />
                      </ActionIcon>
                    </Group>
                  );
                })}
              </Stack>

              {requiredServices.length ? (
                <Stack mt={"1rem"}>
                  <Title order={3} fw={600} fz={16}>
                    {t("required-services")}
                  </Title>

                  <Alert
                    variant="light"
                    color={"blue"}
                    radius={"sm"}
                    maw={drawerWidth}
                    icon={<IconInfoCircle />}>
                    <Text fz={14} c={"blue"}>
                      {t("required-services-note")}
                    </Text>
                  </Alert>

                  <Group
                    pb={"1rem"}
                    justify="space-between"
                    pos={"relative"}
                    style={{
                      borderBottom: "1px solid var(--mantine-color-gray-2)",
                    }}>
                    <Group gap="0.75rem">
                      <div className={classes["icon-wrapper"]}>
                        <IconVaccine size={23} stroke={1.5} color="white" />
                      </div>
                      <Stack gap={3} className={`${classes["text-wrapper"]}`}>
                        <Text fz={15} fw={500} className={`${classes.name}`}>
                          {t("blood-collection")}
                        </Text>
                        <Text fz={14} c="dimmed">
                          {formatCurrency(
                            collectionPrice,
                            i18n.language,
                            currency
                          )}
                        </Text>
                      </Stack>
                    </Group>
                  </Group>

                  <Text fz={14} c="dimmed">
                    <Text inherit fw={600} span>
                      {t("required-for")}:
                    </Text>{" "}
                    {requiredServices.join(", ")}
                  </Text>
                </Stack>
              ) : null}

              <Group justify="space-between" mt={"1rem"}>
                <Text fw={500} fz={15}>
                  {t("subtotal")}:
                </Text>
                <Text fw={500} fz={15}>
                  {formatCurrency(
                    subTotal + (requiredServices.length ? collectionPrice : 0),
                    i18n.language,
                    currency
                  )}
                </Text>
              </Group>
              <ProceedToBookButton
                loading={navigation.state === "loading"}
                labs={allLabs ?? []}
                selectedLabs={selectedLabs}
              />
            </Stack>
          </Drawer>

          <button
            className={`${classes.counter} ${
              selectedLabs.length ? classes.appear : ""
            }`}
            onClick={open}>
            <IconPlus size={25} stroke={2} />
            <span>{selectedLabs.length}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

interface ProceedToBookButtonProps extends ButtonProps {
  selectedLabs: string[];
  labs: ComboboxItemPrice[];
}

const ProceedToBookButton = ({
  selectedLabs,
  labs,
  ...rest
}: ProceedToBookButtonProps) => {
  const { t } = useTranslation();

  const refinedSelectedLabs = useMemo(() => {
    return selectedLabs.reduce((acc, value) => {
      const foundLab = labs.find(lab => lab.value === value);
      if (foundLab) acc.push(foundLab);
      return acc;
    }, [] as typeof labs);
  }, [selectedLabs, labs]);

  return (
    <Button
      radius="xl"
      component={Link}
      to={`book`}
      state={{
        selectedLabs: refinedSelectedLabs,
      }}
      {...rest}>
      {t("proceed-to-booking")}
    </Button>
  );
};

export const loader: LoaderFunction = async ({ request, params }) => {
  const lang = params.lang ?? "";
  const locale = i18n.supportedLngs.includes(lang.toLocaleLowerCase())
    ? lang
    : await i18nextServer.getLocale(request);
  const origin = new URL(request.url).origin;

  const currencyContext = await resolveCurrencyContext(request, true);
  const currency = currencyContext?.currency ?? "XOF";
  const fxRate = currencyContext?.fxRate ?? 1;

  const labs = await getLabServices();
  if (labs.data) {
    labs.data = (
      labs.data?.sort((a: Lab, b: Lab) =>
        a.label.toLocaleLowerCase().localeCompare(b.label.toLocaleLowerCase())
      ) as Lab[]
    ).map((item: Lab) => ({
      ...item,
      originalPrice: item.price, // Store original XOF price
      price: item.price * fxRate // Display converted price
    }));
  }

  // console.log(labs);

  // * NB: Translation doesn't work for meta tags after deployment
  const text =
    locale === "fr"
      ? `Réservez dès maintenant votre rendez-vous en laboratoire en Côte d'Ivoire !`
      : `Book a lab appointment in Ivory Coast (Côte d'Ivoire) now!`;

  const title = `${text} • ${PAGE_TITLE}`;
  const description =
    locale === "fr"
      ? "Sélectionnez les analyses de laboratoire médicales que vous souhaitez effectuer, où que vous soyez en Côte d'Ivoire, ajoutez-les à votre panier et confirmez votre rendez-vous. Nous prélevons les échantillons à votre domicile pour une expérience simple et sécurisée."
      : "Select your medical lab tests anywhere in Ivory Coast (Côte d'Ivoire), add them to your cart, and confirm your appointment. We collect samples at your home for a simple, safe experience.";

  return {
    title,
    description,
    locale,
    origin,
    ok: labs.ok,
    data: labs.data,
    error: labs.error,
    currency,
    fxRate,
    collectionPrice: COLLECTION_PRICE * fxRate,
  };
};
